import { useState, useCallback } from 'react';
import { Button, Table, Form, Modal, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiPlus, HiTrash } from 'react-icons/hi2';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { InlineEdit } from '@/components/common/InlineEdit';
import {
  ColorSwatch,
  ColCol,
  ColOrder,
  ColDefault,
  ColActions,
  ColorPickerInput,
} from './StatusDefinitionsAdmin.styled';
import {
  useListSprawaStatusDefinitionsQuery,
  useCreateSprawaStatusDefinitionMutation,
  useUpdateSprawaStatusDefinitionMutation,
  useDeleteSprawaStatusDefinitionMutation,
} from '@/store/sprawyApi';
import type { SprawaStatusDefinitionDto } from '@/types';

const DEFAULT_COLORS = ['#6c757d', '#0d6efd', '#198754', '#ffc107', '#dc3545', '#0dcaf0', '#6f42c1'];

interface FormState {
  name: string;
  displayOrder: number;
  color: string;
  isDefault: boolean;
}

const EMPTY_FORM: FormState = { name: '', displayOrder: 0, color: '#6c757d', isDefault: false };

export default function StatusDefinitionsAdmin() {
  const { t } = useTranslation();
  const { data: definitions, isLoading } = useListSprawaStatusDefinitionsQuery();
  const [createDef] = useCreateSprawaStatusDefinitionMutation();
  const [updateDef] = useUpdateSprawaStatusDefinitionMutation();
  const [deleteDef] = useDeleteSprawaStatusDefinitionMutation();

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState<SprawaStatusDefinitionDto | null>(null);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, displayOrder: (definitions?.length ?? 0) });
    setShowModal(true);
  };

  const handleSubmit = async () => {
    await createDef(form);
    setShowModal(false);
  };

  const handleSaveField = useCallback(async (defId: string, def: SprawaStatusDefinitionDto, field: string, value: string | number | boolean) => {
    await updateDef({
      id: defId,
      name: def.name,
      displayOrder: def.displayOrder,
      color: def.color,
      isDefault: def.isDefault,
      [field]: field === 'displayOrder' ? Number(value) : value,
    }).unwrap();
  }, [updateDef]);

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteDef(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="card-title mb-1">{t('admin.statusDefinitions.title')}</h5>
            <small className="text-muted">{t('admin.statusDefinitions.subtitle')}</small>
          </div>
          <Button variant="primary" size="sm" onClick={openCreate}>
            <HiPlus size="0.875rem" className="me-1" />
            {t('admin.statusDefinitions.add')}
          </Button>
        </div>

        {isLoading && (
          <div className="text-center py-4"><Spinner /></div>
        )}

        {definitions && definitions.length === 0 && (
          <p className="text-muted text-center py-3">{t('admin.statusDefinitions.empty')}</p>
        )}

        {definitions && definitions.length > 0 && (
          <Table responsive hover size="sm">
            <thead>
              <tr>
                <ColCol>{t('admin.statusDefinitions.color')}</ColCol>
                <th>{t('admin.statusDefinitions.name')}</th>
                <ColOrder>{t('admin.statusDefinitions.order')}</ColOrder>
                <ColDefault>{t('admin.statusDefinitions.isDefault')}</ColDefault>
                <ColActions />
              </tr>
            </thead>
            <tbody>
              {definitions.map((def) => (
                <tr key={def.id}>
                  <td>
                    <InlineEdit
                      value={def.color}
                      onSave={(val) => handleSaveField(def.id, def, 'color', val)}
                      variant="color"
                      colorPresets={DEFAULT_COLORS}
                      ariaLabel={t('admin.statusDefinitions.color')}
                    />
                  </td>
                  <td>
                    <InlineEdit
                      value={def.name}
                      onSave={(val) => handleSaveField(def.id, def, 'name', val)}
                      variant="text"
                      required
                      ariaLabel={t('admin.statusDefinitions.name')}
                    />
                  </td>
                  <td>
                    <InlineEdit
                      value={def.displayOrder}
                      onSave={(val) => handleSaveField(def.id, def, 'displayOrder', val)}
                      variant="number"
                      min={0}
                      ariaLabel={t('admin.statusDefinitions.order')}
                    />
                  </td>
                  <td>
                    <InlineEdit
                      value={def.isDefault}
                      onSave={(val) => handleSaveField(def.id, def, 'isDefault', val)}
                      variant="switch"
                      ariaLabel={t('admin.statusDefinitions.isDefault')}
                    />
                  </td>
                  <td>
                    <Button variant="outline-danger" size="sm" onClick={() => setDeleteTarget(def)}>
                      <HiTrash size="0.75rem" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            {t('admin.statusDefinitions.add')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.statusDefinitions.name')} *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </Form.Group>
          <div className="row">
            <Form.Group className="mb-3 col-6">
              <Form.Label>{t('admin.statusDefinitions.color')}</Form.Label>
              <div className="d-flex gap-2 align-items-center">
                <ColorPickerInput
                  value={form.color}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                />
                <div className="d-flex gap-1">
                  {DEFAULT_COLORS.map((c) => (
                    <ColorSwatch
                      key={c}
                      $color={c}
                      $selected={form.color === c}
                      onClick={() => setForm({ ...form, color: c })}
                    />
                  ))}
                </div>
              </div>
            </Form.Group>
            <Form.Group className="mb-3 col-3">
              <Form.Label>{t('admin.statusDefinitions.order')}</Form.Label>
              <Form.Control
                type="number"
                value={form.displayOrder}
                onChange={(e) => setForm({ ...form, displayOrder: parseInt(e.target.value) || 0 })}
              />
            </Form.Group>
            <Form.Group className="mb-3 col-3 d-flex align-items-end">
              <Form.Check
                type="switch"
                label={t('admin.statusDefinitions.isDefault')}
                checked={form.isDefault}
                onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
              />
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>{t('confirm.cancel')}</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!form.name.trim()}>
            {t('admin.statusDefinitions.add')}
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('admin.statusDefinitions.deleteTitle')}
        message={t('admin.statusDefinitions.deleteConfirm', { name: deleteTarget?.name })}
        confirmLabel={t('confirm.delete')}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
